export { booleanEnvSchema, booleanEnvWithDefault } from "./env";
export { serializeAddress, validateCryptoAddress } from "./address-core";
export * from "./indexed-network-registry";
import { serializeAddress, validateCryptoAddress } from "./address-core";
import {
  Builder,
  canonicalizeAssetIdLike,
  EVM_NATIVE_SLIP44_COIN_TYPE,
  SOLANA_NATIVE_SLIP44_COIN_TYPE,
  normalizeAccountAddress,
  Parser,
  type AccountId,
  type ChainNamespace,
  type ChainReference,
} from "@repo/caip";
import {
  eip155RoleRpcEnvName,
  eip155RpcEnvName,
  getRuntimeEnvRecord,
  readFirstRuntimeEnvValue,
  resolveRuntimeActiveEvmChainIdsFromEnv,
  solanaMainnetRpcEnvName,
  solanaRpcEnvName,
} from "./env";
import { GliderWorkflowType, SOLANA_CHAIN_ID } from "@repo/types";
import invariant from "tiny-invariant";
import {
  type Chain,
  createPublicClient,
  type Hex,
  http,
  parseEther,
  parseUnits,
  zeroAddress,
} from "viem";
import {
  arbitrum,
  base,
  blast,
  bsc,
  linea,
  mainnet,
  optimism,
  plumeMainnet,
  polygon,
  unichain,
} from "viem/chains";

export const ATA_ACCOUNT_SIZE = 165;
export const ETH_MAINNET_CHAIN_ID = mainnet.id;
export const ETH_MAINNET_REBALANCE_COOLDOWN_MS = 86_400_000;
export const ETH_MAINNET_MIN_TRADE_USD = 10;
export const ETH_MAINNET_MIN_WITHDRAW_USD = 10;
export const LEGACY_PLUME_CHAIN_ID = plumeMainnet.id;
export const LEGACY_PLUME_CHAIN_ID_STRING = LEGACY_PLUME_CHAIN_ID.toString(10);

export const normalizeChainIdForComparison = (
  chainId: unknown,
): string | null => {
  if (typeof chainId === "number" && Number.isFinite(chainId)) {
    return Math.trunc(chainId).toString(10);
  }
  if (typeof chainId === "bigint") {
    return chainId.toString(10);
  }
  if (typeof chainId !== "string") {
    return null;
  }

  const trimmed = chainId.trim();
  if (!trimmed) {
    return null;
  }

  if (trimmed.startsWith("eip155:")) {
    const normalized = trimmed.slice("eip155:".length);
    return /^\d+$/.test(normalized) ? normalized : null;
  }

  return /^\d+$/.test(trimmed) ? trimmed : null;
};

export const isLegacyPlumeChainId = (chainId: unknown): boolean =>
  normalizeChainIdForComparison(chainId) === LEGACY_PLUME_CHAIN_ID_STRING;

export const isLegacyPlumePortfolioPrimaryChainId = (
  primaryChainId: unknown,
): boolean => isLegacyPlumeChainId(primaryChainId);

export const isEthMainnetChainId = (chainId: unknown): boolean => {
  if (typeof chainId === "number" && Number.isFinite(chainId)) {
    return Math.trunc(chainId) === ETH_MAINNET_CHAIN_ID;
  }
  if (typeof chainId === "bigint") {
    return chainId === BigInt(ETH_MAINNET_CHAIN_ID);
  }
  if (typeof chainId !== "string") {
    return false;
  }

  const trimmed = chainId.trim();
  if (!trimmed) {
    return false;
  }
  const normalized = trimmed.startsWith("eip155:")
    ? trimmed.slice("eip155:".length)
    : trimmed;
  if (!/^\d+$/.test(normalized)) {
    return false;
  }

  return Number(normalized) === ETH_MAINNET_CHAIN_ID;
};

type AddressType = "solana" | "evm";

/**
 * Validate and normalize a crypto address.
 *
 * Validates the address format and normalizes it:
 * - EVM addresses are lowercased
 * - Solana addresses are kept unchanged (case-sensitive)
 *
 * Uses a discriminated union return type for type-safe narrowing.
 *
 * @param address - The address to validate and normalize
 * @returns Discriminated union:
 *          - `{ isValid: true, address: string, type: AddressType }` if valid
 *          - `{ isValid: false }` if invalid
 *
 */
export const validateAndNormalizeAddress = (
  address: string,
):
  | {
      isValid: true;
      address: string;
      type: AddressType;
    }
  | {
      isValid: false;
    } => {
  const validatedAddress = validateCryptoAddress(address);
  if (!validatedAddress.isValid) {
    return {
      isValid: false,
    };
  }
  switch (validatedAddress.type) {
    case "solana":
      return {
        isValid: true,
        address: address,
        type: validatedAddress.type,
      };
    case "evm":
    default:
      return {
        isValid: true,
        address: address.toLowerCase(),
        type: validatedAddress.type,
      };
  }
};

export const validateAndNormalizeCaipAccountId = (
  caipAccountId: string,
):
  | {
      isValid: true;
      caipAccountId: AccountId;
      chainNamespace: ChainNamespace;
      chainReference: ChainReference;
      address: string;
    }
  | {
      isValid: false;
    } => {
  try {
    const parsed = Parser.parseAccountId(caipAccountId);

    if (
      parsed.chain.namespace === "eip155" ||
      parsed.chain.namespace === "solana"
    ) {
      const normalizedAddress = validateAndNormalizeAddress(
        parsed.account.address,
      );
      if (!normalizedAddress.isValid) {
        return { isValid: false };
      }

      if (
        (parsed.chain.namespace === "eip155" &&
          normalizedAddress.type !== "evm") ||
        (parsed.chain.namespace === "solana" &&
          normalizedAddress.type !== "solana")
      ) {
        return { isValid: false };
      }

      const normalizedCaipAccountId = Builder.buildAccountId(parsed.chain, {
        address: normalizedAddress.address,
      });

      return {
        isValid: true,
        caipAccountId: normalizedCaipAccountId,
        chainNamespace: parsed.chain.namespace,
        chainReference: parsed.chain.reference,
        address: normalizedAddress.address,
      };
    }

    if (parsed.chain.namespace === "sui") {
      return { isValid: false };
    }

    const normalizedAddress = normalizeAccountAddress(
      parsed.chain.namespace,
      parsed.account.address,
    );
    const normalizedCaipAccountId = Builder.buildAccountId(parsed.chain, {
      address: normalizedAddress,
    });

    return {
      isValid: true,
      caipAccountId: normalizedCaipAccountId,
      chainNamespace: parsed.chain.namespace,
      chainReference: parsed.chain.reference,
      address: normalizedAddress,
    };
  } catch {
    return { isValid: false };
  }
};

import {
  HYPEREVM_CHAIN_ID,
  hyperevmChain,
  MONAD_CHAIN_ID,
  monadChain,
  ROBINHOOD_CHAIN_ID,
  robinhoodChain,
  SOLVER_API_KEY,
  WSOL_SOLANA_ADDRESS,
} from "@repo/constants";

// Re-export constants from @repo/constants
export {
  HYPEREVM_CHAIN_ID,
  hyperevmChain,
  MONAD_CHAIN_ID,
  monadChain,
  ROBINHOOD_CHAIN_ID,
  robinhoodChain,
  SOLVER_API_KEY,
  WSOL_SOLANA_ADDRESS,
};

// Re-export SOLANA_CHAIN_ID from @repo/types for convenience
// Also export as SOLANA_NETWORK_ID for backwards compatibility
export { SOLANA_CHAIN_ID } from "@repo/types";
export const SOLANA_NETWORK_ID = SOLANA_CHAIN_ID;

// Alias for the wrapped SOL address
export const SOLANA_NATIVE_TOKEN_ADDRESS = WSOL_SOLANA_ADDRESS;

export enum Addresses {
  NATIVE_TOKEN = "0xeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee",
  ZERO_ADDRESS = "0x0000000000000000000000000000000000000000",
}

export const DEFAULT_USD_AMOUNT_IF_NOT_FUNDED_TO_SANDBOX = 100;

// export const Uint = {
//   MAX_256: 2n ** 256n - 1n,
// };

export const DEFAULT_NEW_STRATEGY_NAME = "Untitled Strategy";

export const GLOBAL_INVITE_CODES = [
  {
    code: "glidercon",
    userGroupName: "DevCon attendee",
  },
  {
    code: "anafam",
    userGroupName: "Anagram friends and family",
  },
  {
    code: "permissionless",
    userGroupName: "Permissionless attendee",
  },
  {
    code: "b@b",
    userGroupName: "Blockchain at Berkeley member",
  },
  {
    code: "berkeley",
    userGroupName: "Blockchain at Berkeley member",
  },
  {
    code: "token2049",
    userGroupName: "Token 2049 friends and family",
  },
  {
    code: "ILoveTrees",
    userGroupName: "Environmental enthusiast",
  },
  {
    code: "GlideCornell",
    userGroupName: "Cornell University member",
  },
  {
    code: "GoBears!",
    userGroupName: "UC Berkeley member",
  },
  {
    code: "GlideUT",
    userGroupName: "University of Texas member",
  },
  {
    code: "GlideVandy",
    userGroupName: "Vanderbilt University member",
  },
  {
    code: "GlidePenn",
    userGroupName: "University of Pennsylvania member",
  },
  {
    code: "GlideMichigan",
    userGroupName: "University of Michigan member",
  },
  {
    code: "GlideWaterloo",
    userGroupName: "University of Waterloo member",
  },
  {
    code: "GlideUVA",
    userGroupName: "University of Virginia member",
  },
  {
    code: "GlideUIUC",
    userGroupName: "University of Illinois member",
  },
  {
    code: "GlideEmory",
    userGroupName: "Emory University member",
  },
  {
    code: "GlideUSC",
    userGroupName: "USC member",
  },
  {
    code: "CalTechSucks",
    userGroupName: "MIT enthusiast",
  },
  {
    code: "BigRed",
    userGroupName: "Cornell University member",
  },
  {
    code: "Quakers",
    userGroupName: "University of Pennsylvania member",
  },
  {
    code: "Trojans!",
    userGroupName: "USC member",
  },
  {
    code: "Trojans",
    userGroupName: "USC member",
  },
  {
    code: "GlideDal",
    userGroupName: "Glider student",
  },
];

// TODO(johnrjj) - Move to env
// Constants for the app
export const DEFINED_API_ROOT_URI = "https://graph.codex.io/graphql";
export const VAULTS_FYI_API_KEY = "3GrOxTzCjvHwWvswZce161GCakqJG7KvOi4OatLPFMA";

export const ONEINCH_API_URL = "https://api.1inch.exchange/v3.0/1/swap";
export const ONEINCH_API_KEY = "5h7Cskz9tU1SsR36g8fHTk7J8kgc1sjH";

export const LIFI_API_ROOT_URI = "https://li.quest/v1";
export const ZEROEX_API_ROOT_URI = "https://api.0x.org";

// TODO(johnrjj) - Move to env
export const ENSO_API_KEY = "4f801458-98e4-4e04-8527-5bec85465797";
export const CHAINALYSIS_API_KEY =
  "7f7e1b22889850992cee6ba7ed412bb2b9afa0a66e3bcc66f81fd47b99ef9855";

export const IMAGEPROXY_ROOT_URL = "https://imageproxy.glider.fi";

// Cash icon URL for empty portfolio states
export const CASH_ICON_URL = "https://cdn.glider.fi/cash-icon.svg";

export const NATIVE_TOKEN_INTERNAL_PLACEHOLDER_ADDRESS =
  "0xeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee" as const;

export const ZERO_ADDRESS = zeroAddress;

/**
 * Bind an asset identifier to its execution chain and canonicalize the key
 * used by execution balance holds. EVM native value has a single hold key:
 * `eip155:<chain>/slip44:60`, even when an execution provider represents it
 * with the internal `0xeeee...eeee` ERC-20 sentinel.
 *
 * EVM erc20 references are validated as real 20-byte addresses and the zero
 * address is REJECTED (throws): the EVM balance reader does not execute it as
 * native and no ERC-20 lives there, so a hold stored under either key could
 * never match a read — it would claim protection it cannot provide. Callers
 * at admission catch and route to the fail-closed unreadable path; the hold
 * write boundary maps the throw to its typed identifier error.
 */
export const canonicalizeHoldAssetId = (
  chainRef: string,
  assetIdOrTokenAddress: string,
): string => {
  const parsedChain = Parser.parseChainId(chainRef).chain;
  const scopedChain = {
    namespace: parsedChain.namespace,
    reference:
      parsedChain.namespace === "eip155"
        ? parsedChain.reference.toLowerCase()
        : parsedChain.reference,
  };
  const candidateAssetId = assetIdOrTokenAddress.includes("/")
    ? assetIdOrTokenAddress
    : Builder.buildAssetId(scopedChain, {
        namespace: "erc20",
        reference: assetIdOrTokenAddress,
      });
  const parsedAsset = Parser.parseAssetId(
    canonicalizeAssetIdLike(candidateAssetId),
  ).asset;
  const scopedAsset =
    scopedChain.namespace === "eip155" &&
    parsedAsset.namespace === "erc20" &&
    parsedAsset.reference.toLowerCase() ===
      NATIVE_TOKEN_INTERNAL_PLACEHOLDER_ADDRESS
      ? {
          namespace: "slip44" as const,
          reference: EVM_NATIVE_SLIP44_COIN_TYPE,
        }
      : parsedAsset;

  if (scopedChain.namespace === "eip155" && scopedAsset.namespace === "erc20") {
    if (!/^0x[a-f0-9]{40}$/.test(scopedAsset.reference)) {
      throw new Error(
        `hold_asset_id_invalid: erc20 reference is not a 20-byte address: ${scopedAsset.reference}`,
      );
    }
    if (scopedAsset.reference === ZERO_ADDRESS) {
      throw new Error(
        "hold_asset_id_invalid: zero-address token is neither executable erc20 nor a recognized native alias",
      );
    }
  }
  // EVM native has exactly one canonical key. The execution resolver treats
  // ANY slip44 transfer as native, while readers query slip44:60 — a hold
  // under slip44:1 or slip44:060 would be a distinct, never-matched key.
  if (
    scopedChain.namespace === "eip155" &&
    scopedAsset.namespace === "slip44" &&
    scopedAsset.reference !== EVM_NATIVE_SLIP44_COIN_TYPE
  ) {
    throw new Error(
      `hold_asset_id_invalid: eip155 slip44 reference must be ${EVM_NATIVE_SLIP44_COIN_TYPE}: ${scopedAsset.reference}`,
    );
  }
  // A hold key must be executable on its own chain: readers construct keys
  // from real executable assets, so a CAIP-valid but VM-inconsistent pairing
  // (eip155 + spl, solana + erc20) would persist a reservation nothing can
  // ever match. Only the two known VMs are constrained — future namespaces
  // stay open per the vm-agnostic rule.
  if (
    scopedChain.namespace === "eip155" &&
    scopedAsset.namespace !== "erc20" &&
    scopedAsset.namespace !== "slip44"
  ) {
    throw new Error(
      `hold_asset_id_invalid: eip155 assets must be erc20 or native slip44, got ${scopedAsset.namespace}`,
    );
  }
  if (scopedChain.namespace === "solana") {
    if (scopedAsset.namespace === "slip44") {
      if (scopedAsset.reference !== SOLANA_NATIVE_SLIP44_COIN_TYPE) {
        throw new Error(
          `hold_asset_id_invalid: solana slip44 reference must be ${SOLANA_NATIVE_SLIP44_COIN_TYPE}: ${scopedAsset.reference}`,
        );
      }
    } else if (scopedAsset.namespace !== "spl") {
      throw new Error(
        `hold_asset_id_invalid: solana assets must be spl or native slip44, got ${scopedAsset.namespace}`,
      );
    }
  }

  return Builder.buildAssetId(scopedChain, scopedAsset);
};

// Wrapped native token addresses for each chain
const WETH_ARBITRUM_ADDRESS = "0x82af49447d8a07e3bd95bd0d56f35241523fbab1";
const WETH_BASE_ADDRESS = "0x4200000000000000000000000000000000000006";
const WETH_POLYGON_ADDRESS = "0x7ceb23fd6bc0add59e62ac25578270cff1b9f619";
const WMATIC_POLYGON_ADDRESS = "0x0d500b1d8e8ef31e21c99d1db9a6444d3adf1270";
const WBNB_BSC_ADDRESS = "0xbb4CdB9CBd36B01bD1cBaEBF2De08d9173bc095c";
const WETH_OPTIMISM_ADDRESS = "0x4200000000000000000000000000000000000006";
const WETH_BLAST_ADDRESS = "0x4300000000000000000000000000000000000004";
const WETH_MAINNET_ADDRESS = "0xc02aaa39b223fe8d0a0e5c4f27ead9083c756cc2";
const WETH_LINEA_ADDRESS = "0xe5D7C2a44FfDDf6b295A15c148167daaAf5Cf34f";
const WETH_UNICHAIN_ADDRESS = "0x4200000000000000000000000000000000000006";
const WPLUME_PLUME_ADDRESS = "0xEa237441c92CAe6FC17Caaf9a7acB3f953be4bd1"; // Wrapped PLUME
const WETH_ROBINHOOD_ADDRESS = "0x0Bd7D308f8E1639FAb988df18A8011f41EAcAD73"; // Robinhood Chain (mainnet 4663) WETH (native gas = ETH)
const WHYPE_HYPEREVM_ADDRESS = "0x5555555555555555555555555555555555555555"; // HyperEVM (999) wrapped native HYPE, 18dp
const WMON_MONAD_ADDRESS = "0x3bd359C1119dA7Da1D913D1C4D2B7c461115433A"; // Monad (143) canonical wrapped native MON, 18dp (verified on-chain 2026-08-07)

// USDC addresses for each chain
const USDC_MAINNET_ADDRESS = "0xa0b86991c6218b36c1d19d4a2e9eb0ce3606eb48";
const USDC_BASE_ADDRESS = "0x833589fcd6edb6e08f4c7c32d4f71b54bda02913";
const USDC_ARBITRUM_ADDRESS = "0xaf88d065e77c8cC2239327C5EDb3A432268e5831";
const USDC_OPTIMISM_ADDRESS = "0x0b2c639c533813f4aa9d7837caf62653d097ff85";
const USDC_POLYGON_ADDRESS = "0x3c499c542cef5e3811e1192ce70d8cc03d5c3359";
const USDC_BLAST_ADDRESS = "0x4300000000000000000000000000000000000003";
const USDC_LINEA_ADDRESS = "0x176211869ca2b568f2a7d4ee941e073a821ee1ff";
const USDC_BSC_ADDRESS = "0x8AC76a51cc950d9822D68b83fE1Ad97B32Cd580d";
const USDC_PLUME_ADDRESS = "0x222365EF19F7947e5484218551B56bb3965Aa7aF";
// Robinhood Chain's canonical stablecoin is USDG (Global Dollar), not USDC/USDT.
// Threaded through getUsdcAddressForChainId so the funding/cash leg, stablecoin-swap
// detection and withdraw-as-USDC all recognize it. Decimals verified on-chain.
const USDG_ROBINHOOD_ADDRESS = "0x5fc5360D0400a0Fd4f2af552ADD042D716F1d168";
// HyperEVM's cash asset is NATIVE Circle USDC (6 decimals), not USDT0: since
// late 2025 USDC dominates the chain's stable liquidity and is HyperCore<>EVM
// linked. Beware scam duplicates on token lists — always match by address.
const USDC_HYPEREVM_ADDRESS = "0xb88339CB7199b77E23DB6E890353E22632Ba630f";
// Monad's cash asset is NATIVE Circle USDC (6 decimals) — deployed by Circle at
// chain launch (Nov 2025) alongside CCTP. Verified on-chain and against
// Circle's announcement. Beware USDT0 and bridged duplicates on token lists —
// always match by address.
const USDC_MONAD_ADDRESS = "0x754704Bc059F8C67012fEd69BC8A327a5aafb603";
const USDC_SOLANA_ADDRESS = "EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v";
// Note: Unichain USDC address not yet available

// USDT addresses for each chain
const USDT_MAINNET_ADDRESS = "0xdac17f958d2ee523a2206206994597c13d831ec7";
const USDT_BASE_ADDRESS = "0xfde4C96c8593536E31F229EA8f37b2ADa2699bb2";
const USDT_ARBITRUM_ADDRESS = "0xfd086bc7cd5c481dcc9c85ebe478a1c0b69fcbb9";
const USDT_OPTIMISM_ADDRESS = "0x94b008aa00579c1307b0ef2c499ad98a8ce58e58";
const USDT_POLYGON_ADDRESS = "0xc2132d05d31c914a87c6611c10748aeb04b58e8f";
const USDT_BSC_ADDRESS = "0x55d398326f99059ff775485246999027b3197955";
const USDT_LINEA_ADDRESS = "0xa219439258ca9da29e9cc4ce5596924745e12b93";

const LIFI_ENTRYPOINT: Hex = "0x1231DEB6f5749EF6cE6943a275A1D3E7486F4EaE";
const LIFI_ROBINHOOD_ENTRYPOINT: Hex =
  "0xb477751b76cf82d00a686a1232f5fcd772414af3";
// LiFi Diamond on HyperEVM (999) — non-canonical deployment (bytecode verified
// on-chain; also reported by li.quest/v1/chains).
const LIFI_HYPEREVM_ENTRYPOINT: Hex =
  "0x0a0758d937d1059c356D4714e57F5df0239bce1A";
// LiFi Diamond on Monad (143) — non-canonical deployment (bytecode verified
// on-chain; also reported by li.quest/v1/chains).
const LIFI_MONAD_ENTRYPOINT: Hex =
  "0x026F252016A7C47CDEf1F05a3Fc9E20C92a49C37";

// export type ENTRYPOINT_ADDRESS_V07_TYPE =
//   "0x0000000071727De22E5E9d8BAf0edAc6f37da032";
// const SMART_CONTRACT_ENTRYPOINT_ADDRESS_KERNEL_V07: ENTRYPOINT_ADDRESS_V07_TYPE =
//   "0x0000000071727De22E5E9d8BAf0edAc6f37da032";
// export const SMART_CONTRACT_ENTRYPOINT =
//   SMART_CONTRACT_ENTRYPOINT_ADDRESS_KERNEL_V07;
export const SMART_CONTRACT_VENDOR = "kernel";
export const SMART_CONTRACT_KERNEL_VERSION_DEFAULT = "0.3.1";

export const DEFAULT_CHAIN = base;
export const DEFAULT_CHAIN_ID = base.id;
export const DEFAULT_ONBOARDING_CHAIN = base;

export const NOVU_API_ENDPOINT_ROOT = "https://api.novu.co/v1";

export const getDefaultChain = () => {
  return DEFAULT_CHAIN;
};

export const getDefaultOnboardingChain = () => {
  return DEFAULT_ONBOARDING_CHAIN;
};

export const CHAINS_TO_ENABLE_SESSION_KEY_FOR_DEFAULT = Object.freeze([
  polygon.id,
  base.id,
  arbitrum.id,
  optimism.id,
  blast.id,
  mainnet.id,
  linea.id,
  bsc.id,
  // Robinhood Chain activation: enrollment derives a kernel account on every
  // chain in this list. 4663's on-chain derivation returns address(0) — the
  // pinned local CREATE2 fallback in @repo/vaults createVaultAccount handles
  // it — and its backend RPC/bundler env must ship in the same release
  // (RPC_EIP155_4663_* / RUNTIME_ACTIVE_EVM_CHAIN_IDS).
  robinhoodChain.id, // Robinhood Chain (4663)
  // HyperEVM activation: the ECDSA Kernel module set is deployed on 999, but
  // the canonical Smart Account validator required by SAFE/ERC1271 vaults is
  // not. Deploy it and ship the backend RPC/bundler/paymaster env
  // (RPC_EIP155_999_* / RUNTIME_ACTIVE_EVM_CHAIN_IDS) before marking the chain
  // runtime-active; engine startup preflight intentionally fails closed.
  hyperevmChain.id, // HyperEVM (999)
  // Monad activation: full Kernel module set verified deployed on 143 —
  // including BOTH sudo validators (multi-chain ECDSA and Smart Account for
  // SAFE/ERC1271) — via kernel-module-preflight, 8/8 DEPLOYED (2026-08-06).
  // Backend RPC/bundler/paymaster env must ship in the same release
  // (RPC_EIP155_143_* / RUNTIME_ACTIVE_EVM_CHAIN_IDS) before the chain is
  // marked runtime-active.
  monadChain.id, // Monad (143)
  // Note: Unichain (130) intentionally excluded - requires pre-migration
]);
export const SESSION_KEY_SUPPORTED_CHAIN_IDS =
  CHAINS_TO_ENABLE_SESSION_KEY_FOR_DEFAULT;

// Runtime-active EVM execution chains for the currently shipped EVM rollout.
// Keep this list intentionally small and use it from runtime services to avoid drift.
export const RUNTIME_ACTIVE_EVM_CHAIN_IDS = Object.freeze(
  resolveRuntimeActiveEvmChainIdsFromEnv(),
);
export const RUNTIME_ACTIVE_EVM_CHAIN_ID_STRINGS = Object.freeze(
  RUNTIME_ACTIVE_EVM_CHAIN_IDS.map((chainId) => chainId.toString(10)),
);

// Coinbase Smart Wallet (ERC1271) session keys require single-chain signing.
// Base and BNB have the canonical ZeroDev smart-account validator deployed;
// keep Ethereum mainnet as an explicit opt-in.
export const COINBASE_WALLET_SESSION_KEY_DEFAULT_CHAIN_IDS = Object.freeze([
  base.id,
  bsc.id,
]);
export const COINBASE_WALLET_SESSION_KEY_OPTIONAL_CHAIN_IDS = Object.freeze([
  mainnet.id,
]);

export interface CoinbaseWalletSessionKeyChainOptions {
  includeEthereumMainnet?: boolean;
  extraChainIds?: readonly number[];
}

export const getCoinbaseWalletSessionKeyChainIds = (
  options: CoinbaseWalletSessionKeyChainOptions = {},
): number[] => {
  const chainIds: number[] = [...COINBASE_WALLET_SESSION_KEY_DEFAULT_CHAIN_IDS];

  if (options.includeEthereumMainnet) {
    chainIds.push(...COINBASE_WALLET_SESSION_KEY_OPTIONAL_CHAIN_IDS);
  }

  if (options.extraChainIds) {
    for (const chainId of options.extraChainIds) {
      if (Number.isInteger(chainId) && chainId > 0) {
        chainIds.push(chainId);
      }
    }
  }

  return Array.from(new Set(chainIds));
};

// Legacy export retained for existing callers; defaults to Base and BNB.
export const CHAINS_TO_ENABLE_SESSION_KEY_FOR_COINBASE_WALLET =
  COINBASE_WALLET_SESSION_KEY_DEFAULT_CHAIN_IDS;

// Coinbase bundler is intentionally Base-only for now.
export const COINBASE_BUNDLER_SUPPORTED_CHAIN_IDS = Object.freeze([base.id]);
export const COINBASE_BUNDLER_SUPPORTED_CHAIN_ID_STRINGS = Object.freeze(
  COINBASE_BUNDLER_SUPPORTED_CHAIN_IDS.map((chainId) => chainId.toString(10)),
);
export const isCoinbaseBundlerChainSupported = (
  chainId: string | number,
): boolean =>
  COINBASE_BUNDLER_SUPPORTED_CHAIN_ID_STRINGS.includes(chainId.toString(10));

/**
 * For use throughout the app when we need to reference all chain ids.
 */
export const ALL_CHAIN_IDS = Object.freeze([
  ...new Set(CHAINS_TO_ENABLE_SESSION_KEY_FOR_DEFAULT),
]);
export type ALL_CHAIN_IDS_TYPE = (typeof ALL_CHAIN_IDS)[number];

// Merge this with the ALL chains list?
// Chains broadly supported by backend services (pricing, discovery, etc.)
export const SUPPORTED_CHAINS = [
  { id: mainnet.id, name: "Ethereum", symbol: "ETH" },
  { id: optimism.id, name: "Optimism", symbol: "ETH" },
  { id: polygon.id, name: "Polygon", symbol: "MATIC" },
  { id: bsc.id, name: "BNB Chain", symbol: "BNB" },
  { id: arbitrum.id, name: "Arbitrum", symbol: "ETH" },
  { id: base.id, name: "Base", symbol: "ETH" },
] as const;

export const SUPPORTED_CHAIN_IDS = SUPPORTED_CHAINS.map((chain) => chain.id);
export const SUPPORTED_CHAIN_ID_STRINGS = SUPPORTED_CHAINS.map((chain) =>
  chain.id.toString(10),
);

// App-available chains (UI-facing; subset of all chains we can sign for)
export const APP_SUPPORTED_CHAINS = [
  { id: base.id, name: "Base", symbol: base.nativeCurrency.symbol },
  { id: mainnet.id, name: "Ethereum", symbol: mainnet.nativeCurrency.symbol },
  { id: arbitrum.id, name: "Arbitrum", symbol: arbitrum.nativeCurrency.symbol },
  { id: bsc.id, name: "BNB Chain", symbol: bsc.nativeCurrency.symbol },
  {
    id: robinhoodChain.id,
    name: "Robinhood Chain",
    symbol: robinhoodChain.nativeCurrency.symbol,
  },
  {
    id: hyperevmChain.id,
    name: "HyperEVM",
    symbol: hyperevmChain.nativeCurrency.symbol,
  },
  {
    id: monadChain.id,
    name: "Monad",
    symbol: monadChain.nativeCurrency.symbol,
  },
  { id: SOLANA_CHAIN_ID, name: "Solana", symbol: "SOL" },
] as const;

type AppSupportedChain = (typeof APP_SUPPORTED_CHAINS)[number];
type AppSupportedEvmChain = Exclude<
  AppSupportedChain,
  { id: typeof SOLANA_CHAIN_ID }
>;

const isAppSupportedEvmChain = (
  chain: AppSupportedChain,
): chain is AppSupportedEvmChain => chain.id !== SOLANA_CHAIN_ID;

export const APP_SUPPORTED_EVM_CHAINS = APP_SUPPORTED_CHAINS.filter(
  isAppSupportedEvmChain,
);

export const APP_DEPOSIT_SUPPORTED_CHAINS = [
  { id: mainnet.id, name: "Ethereum", symbol: "ETH" },
  { id: arbitrum.id, name: "Arbitrum", symbol: "ETH" },
  { id: base.id, name: "Base", symbol: "ETH" },
  { id: bsc.id, name: "BNB Chain", symbol: "BNB" },
  // Must stay in sync with APP_SUPPORTED_EVM_CHAINS (runtime-chain-constants.test.ts
  // asserts deposit-supported EVM set === app-supported EVM set).
  { id: robinhoodChain.id, name: "Robinhood Chain", symbol: "ETH" },
  { id: hyperevmChain.id, name: "HyperEVM", symbol: "HYPE" },
  { id: monadChain.id, name: "Monad", symbol: "MON" },
];

export const APP_SUPPORTED_CHAIN_IDS = APP_SUPPORTED_CHAINS.map(
  (chain) => chain.id,
);
export const APP_SUPPORTED_CHAIN_ID_STRINGS = APP_SUPPORTED_CHAINS.map(
  (chain) => chain.id.toString(10),
);
export const APP_SUPPORTED_EVM_CHAIN_IDS = APP_SUPPORTED_EVM_CHAINS.map(
  (chain) => chain.id,
);
export const APP_SUPPORTED_EVM_CHAIN_ID_STRINGS = APP_SUPPORTED_EVM_CHAINS.map(
  (chain) => chain.id.toString(10),
);

export const APP_DEPOSIT_SUPPORTED_CHAIN_IDS = APP_DEPOSIT_SUPPORTED_CHAINS.map(
  (chain) => chain.id,
);
export const APP_DEPOSIT_SUPPORTED_CHAIN_ID_STRINGS =
  APP_DEPOSIT_SUPPORTED_CHAINS.map((chain) => chain.id.toString(10));

// SVM-specific deposit chains (separate from EVM until cross-chain deposits are supported)
export const APP_DEPOSIT_SUPPORTED_CHAINS_SVM = [
  { id: SOLANA_CHAIN_ID, name: "Solana", symbol: "SOL" },
];

export const APP_DEPOSIT_SUPPORTED_CHAIN_IDS_SVM =
  APP_DEPOSIT_SUPPORTED_CHAINS_SVM.map((chain) => chain.id);
export const APP_DEPOSIT_SUPPORTED_CHAIN_ID_STRINGS_SVM =
  APP_DEPOSIT_SUPPORTED_CHAINS_SVM.map((chain) => chain.id.toString(10));

export const MINIMUM_BALANCE_USD_FOR_REFERRAL_CODE = 10;

export const getChainForChainId = (chainId: string | number): Chain => {
  switch (chainId.toString(10)) {
    case polygon.id.toString(10):
      return polygon;
    case base.id.toString(10):
      return base;
    case arbitrum.id.toString(10):
      return arbitrum;
    case optimism.id.toString(10):
      return optimism;
    case blast.id.toString(10):
      return blast;
    case mainnet.id.toString(10):
      return mainnet;
    case linea.id.toString(10):
      return linea;
    case unichain.id.toString(10):
      return unichain;
    case bsc.id.toString(10):
      return bsc;
    case plumeMainnet.id.toString(10):
      return plumeMainnet;
    case robinhoodChain.id.toString(10):
      return robinhoodChain;
    case hyperevmChain.id.toString(10):
      return hyperevmChain;
    case monadChain.id.toString(10):
      return monadChain;
    default:
      throw new Error(`Unsupported chainId: ${chainId}`);
  }
};

export const getWrappedNativeTokenAddressForChainId = (
  chainId: string | number,
) => {
  switch (chainId.toString(10)) {
    case polygon.id.toString(10):
      return WMATIC_POLYGON_ADDRESS;
    case base.id.toString(10):
      return WETH_BASE_ADDRESS; // weth
    case arbitrum.id.toString(10):
      return WETH_ARBITRUM_ADDRESS; // weth
    case optimism.id.toString(10):
      return WETH_OPTIMISM_ADDRESS;
    case blast.id.toString(10):
      return WETH_BLAST_ADDRESS;
    case mainnet.id.toString(10):
      return WETH_MAINNET_ADDRESS;
    case linea.id.toString(10):
      return WETH_LINEA_ADDRESS;
    case unichain.id.toString(10):
      return WETH_UNICHAIN_ADDRESS;
    case SOLANA_NETWORK_ID.toString():
      return WSOL_SOLANA_ADDRESS;
    case bsc.id.toString(10):
      return WBNB_BSC_ADDRESS;
    case plumeMainnet.id.toString(10):
      return WPLUME_PLUME_ADDRESS;
    case robinhoodChain.id.toString(10):
      return WETH_ROBINHOOD_ADDRESS;
    case hyperevmChain.id.toString(10):
      return WHYPE_HYPEREVM_ADDRESS;
    case monadChain.id.toString(10):
      return WMON_MONAD_ADDRESS;
    default:
      throw new Error(`Unsupported chainId: ${chainId}`);
  }
};

export const getUsdcAddressForChainId = (
  chainId: string | number,
): string | undefined => {
  switch (chainId.toString(10)) {
    case mainnet.id.toString(10):
      return USDC_MAINNET_ADDRESS;
    case base.id.toString(10):
      return USDC_BASE_ADDRESS;
    case arbitrum.id.toString(10):
      return USDC_ARBITRUM_ADDRESS;
    case optimism.id.toString(10):
      return USDC_OPTIMISM_ADDRESS;
    case polygon.id.toString(10):
      return USDC_POLYGON_ADDRESS;
    case blast.id.toString(10):
      return USDC_BLAST_ADDRESS;
    case linea.id.toString(10):
      return USDC_LINEA_ADDRESS;
    // NOTE: USDC on BSC has 18 decimals, not 6 like all other chains.
    case bsc.id.toString(10):
      return USDC_BSC_ADDRESS;
    case unichain.id.toString(10):
      // USDC not yet available on Unichain
      return undefined;
    case plumeMainnet.id.toString(10):
      return USDC_PLUME_ADDRESS;
    // Robinhood Chain has no USDC; USDG is its canonical USD stablecoin.
    case robinhoodChain.id.toString(10):
      return USDG_ROBINHOOD_ADDRESS;
    // HyperEVM: native Circle USDC (6dp) is the canonical cash asset.
    case hyperevmChain.id.toString(10):
      return USDC_HYPEREVM_ADDRESS;
    // Monad: native Circle USDC (6dp) is the canonical cash asset.
    case monadChain.id.toString(10):
      return USDC_MONAD_ADDRESS;
    case String(SOLANA_CHAIN_ID):
      return USDC_SOLANA_ADDRESS;
    default:
      // Return undefined for unsupported chains rather than throwing
      // This allows graceful degradation
      return undefined;
  }
};

export const getUsdtAddressForChainId = (
  chainId: string | number,
): string | undefined => {
  switch (chainId.toString(10)) {
    case mainnet.id.toString(10):
      return USDT_MAINNET_ADDRESS;
    case base.id.toString(10):
      return USDT_BASE_ADDRESS;
    case arbitrum.id.toString(10):
      return USDT_ARBITRUM_ADDRESS;
    case optimism.id.toString(10):
      return USDT_OPTIMISM_ADDRESS;
    case polygon.id.toString(10):
      return USDT_POLYGON_ADDRESS;
    case bsc.id.toString(10):
      return USDT_BSC_ADDRESS;
    case linea.id.toString(10):
      return USDT_LINEA_ADDRESS;
    default:
      return undefined;
  }
};

type LowercaseHexAddress = Lowercase<`0x${string}`>;

export const LIFI_FEE_EXEMPT_WALLET_ADDRESSES: ReadonlySet<LowercaseHexAddress> =
  new Set<LowercaseHexAddress>([
    "0x6c5b6b451ff04335305268563a145090a23da6bd",
    "0xcdc82cd8b5ecb389141e21599a7ff0f7d07e048b",
    "0x8666c1220328349f348d6869c4373e5ea9079c50",
    "0x2f2fc3903a1082863887f560149e06008a6c91cf",
    "0x468ccdf14980849714c98f50c3dd31d97fe607f8",
    "0x136a9e27fca079e77a5e9ed9e353059a113d938a",
    "0x81867dd34d5cde67084b4fec82ea2a90b4f065a0",
    "0xabab1496ff61cb8048e2e475858995771b107369",
    "0x4a295143d2a819bad2324175164e06ebf1c0bc5c",
    "0xa0365df21dcc4d3ca0d31fcbef18718cbbedce92",
    "0x20eb759931ad018ef35e6c6cec039282dcf614e8",
    "0xf9dd2a79edb32e993fc3b1120ccd673ac4594282",
    "0x32bc6cff24ff9f024d1819fb1179143230311ce8",
    "0x547db5b7edb1b9fb547b05f9ffcf421ee27579cf",
    "0xb16afb08134729b68dc83e6e72df983197a33f05",
    "0xa7025e284d9aaa4b6635c783ee8c7b2b6a91f92d",
    "0xd14da917faf3e85863b2305d0d09d0e7f2531016",
    "0xb5c1a69e6cdab37d65ae69f40a0a9c6e8d0d4c69",
    "0x413f6e6c6dd7bad5dd868f789ccf49d7cc512c24",
    "0xf941e0e1b085a3cd41ed094671ea7c9e099a6e14",
    "0xa71b8968375c83be942ecebcd01e78ff49ad0c14",
    "0x5e25a8844ec894a5a390a1025169156cda426b48",
    "0xe238bceefef0f61506911a1c2d56526951bb136f",
    "0xe300d212e6e509ffbd25db204269660531848c3f",
    "0x07de3f3020983530efd6be885d29a89b521273cd",
    "0x2a91a5802b85b773851fa9dc3b4ffe801b8f763b",
    "0xc81f7b9bbd22a92a1e8c6512ff18e7606c0d097d",
    "0x3aedb97569ad578038498af46f071045b1f36476",
    "0xc59d2b0e32c480659b417d17d438092a7f6b5fb6",
    "0x08ac1b5364fe774b46b265b33dae52f62b98023a",
    "0xbbe821840d15704bc53204c62b877508bdab32a7",
    "0x1c5bf9049377a269639022a8edb206c15b47a74e",
    "0x1bb5d06d0099d386e38a57169df40d2685fab44e",
    "0x563031659ae549676de7bd67f89260d888f025e4",
    "0x13c6f3fc315d05b9f407c2bcbca0c5802eaf65dc",
    "0xe2f6aebd4dff1c6593fb0a645d5a6b3ba8111a38",
    "0x69f66bd6628ff27222da37245ed686c3ef4766c9",
    "0x9ba82c668acd8175076f88a12fb234853da49222",
    "0x4cf08426eb6535347aea857e82dc28fa83120050",
    "0xe14b4b1f43be18145f06d3d6c96d9cdeaa59772c",
    "0x993a6d9e36ec633a03a74f1b2f8aa8b8a7c12816",
    "0xf27de7c0d516b5dbc752b0ed13f582fb2d62a0cf",
    "0x0d0265fc919161eb0571dcb19c27789cb0931eff",
    "0x5b31468f254240058ba2eaa299a7cf25bf783523",
    "0xb47b2cfe9ef866e921da3fcc984cc723a760ae91",
    "0xba29c59e2eeeeeeab7b1370ca1c084fd84fb72fe",
    "0x8feb2d554b9962cd37002680125b6edc3150819f",
    "0xd67696b96243602babf96303934b347c7502428b",
    "0xdb4629917e1804c16c9efe0a61b4dfb7568bf651",
    "0xf70fa1b8443c620ca7d43a9d17f4bde8b09031cc",
    "0x39a3ea3460d9d787a26d8aeaa98a44fe6a9f4c83",
    "0x0da7d9d0363c96c02cab0970db5e53233b942488",
    "0xc373d0246812b721eef5420bc026c49b65e9160d",
    "0xd8bbf078fdfc7f7fe6ac4002857f3768b96edc40",
    "0x1ee001922d5b4194eb8fa3342af57027ffd6a2d4",
    "0x1bb5d06d0099d386e38a57169df40d2685fab44e",
    "0x4b33eaa9e03133c9edf0c75573f94482529bf80a",
    "0x9bf63b1de81952e17f8c23afab8f57b8f242d90a",
    "0xdb306c9e485c75b5f84d82a7ccc39ecc9a4b0812",
    "0xf26998bf3a888c17b50ee544c43b063f9f12a239",
    "0xe4a6caf53722900c100a7f93f431b3c244872ab0",
    "0x3dc761d9785ca698657b9ffebc6b6cd5986ee365",
    "0xb4cb46032556339a3e132867d8babd54d34abd45",
    "0x1db75a2841f0468a360a7bdab0b8800ebc095abb",
    "0x10ecb5f21d33c5bc2df7bc71813ddf72a9e5d91c",
  ]);

export const isLifiFeeExemptWalletAddress = (
  walletAddress: string | null | undefined,
): boolean => {
  if (!walletAddress) {
    return false;
  }

  return LIFI_FEE_EXEMPT_WALLET_ADDRESSES.has(
    walletAddress.toLowerCase() as LowercaseHexAddress,
  );
};

/**
 * Check if a token address is a stablecoin (USDC or USDT) on the given chain.
 * Used to prevent points farming via stablecoin-to-stablecoin swaps.
 */
export const isStablecoinAddress = (
  tokenAddress: string,
  chainId: string | number,
): boolean => {
  const normalizedAddress = tokenAddress.toLowerCase();
  const usdcAddress = getUsdcAddressForChainId(chainId)?.toLowerCase();
  const usdtAddress = getUsdtAddressForChainId(chainId)?.toLowerCase();
  return normalizedAddress === usdcAddress || normalizedAddress === usdtAddress;
};

/**
 * Check if a swap is between two stablecoins (USDC <-> USDT).
 * These swaps do not earn points as they don't represent real trading activity.
 */
export const isStablecoinSwap = (
  tokenInAddress: string,
  tokenInChainId: string | number,
  tokenOutAddress: string,
  tokenOutChainId: string | number,
): boolean => {
  return (
    isStablecoinAddress(tokenInAddress, tokenInChainId) &&
    isStablecoinAddress(tokenOutAddress, tokenOutChainId)
  );
};

/**
 * Check if a swap is the explicit same-chain USDC <-> USDT bypass pair.
 * Unlike `isStablecoinSwap`, this only matches the hardcoded USDC/USDT pair
 * and does not treat all stablecoin combinations as fee-exempt.
 */
export const isUsdcUsdtSwapPair = (
  tokenInAddress: string,
  tokenInChainId: string | number,
  tokenOutAddress: string,
  tokenOutChainId: string | number,
): boolean => {
  const inputChainId = tokenInChainId.toString(10);
  const outputChainId = tokenOutChainId.toString(10);
  if (inputChainId !== outputChainId) {
    return false;
  }

  const usdcAddress = getUsdcAddressForChainId(inputChainId)?.toLowerCase();
  const usdtAddress = getUsdtAddressForChainId(inputChainId)?.toLowerCase();
  if (!usdcAddress || !usdtAddress) {
    return false;
  }

  const normalizedInputAddress = tokenInAddress.toLowerCase();
  const normalizedOutputAddress = tokenOutAddress.toLowerCase();

  return (
    (normalizedInputAddress === usdcAddress &&
      normalizedOutputAddress === usdtAddress) ||
    (normalizedInputAddress === usdtAddress &&
      normalizedOutputAddress === usdcAddress)
  );
};

/**
 * Check if a route moves canonical USDC between two supported chains.
 * Used to waive LiFi integrator fees for USDC bridge routes without making all
 * cross-chain stablecoin routes fee-exempt.
 */
export const isUsdcBridgePair = (
  tokenInAddress: string,
  tokenInChainId: string | number,
  tokenOutAddress: string,
  tokenOutChainId: string | number,
): boolean => {
  const inputUsdcAddress =
    getUsdcAddressForChainId(tokenInChainId)?.toLowerCase();
  const outputUsdcAddress =
    getUsdcAddressForChainId(tokenOutChainId)?.toLowerCase();
  if (!inputUsdcAddress || !outputUsdcAddress) {
    return false;
  }

  return (
    tokenInAddress.toLowerCase() === inputUsdcAddress &&
    tokenOutAddress.toLowerCase() === outputUsdcAddress
  );
};

const explorerUrls: Record<string, string> = {
  [polygon.id]: "https://polygonscan.com",
  [base.id]: "https://basescan.org",
  [arbitrum.id]: "https://arbiscan.io",
  [optimism.id]: "https://optimistic.etherscan.io",
  [blast.id]: "https://blastscan.io",
  [mainnet.id]: "https://etherscan.io",
  [linea.id]: "https://lineascan.build",
  [unichain.id]: "https://uniscan.io",
  [SOLANA_NETWORK_ID]: "https://solscan.io",
  [bsc.id]: "https://bscscan.com",
  [plumeMainnet.id]: "https://explorer.plume.org",
  [robinhoodChain.id]: "https://robinhoodchain.blockscout.com",
  [hyperevmChain.id]: "https://hyperevmscan.io",
  // Etherscan-family instance (guaranteed /tx/, /address/ link paths).
  // MonadVision (monadvision.com) is the other first-party explorer.
  [monadChain.id]: "https://monadscan.com",
};

export const getBlockExplorerRootUrl = (chainId: string | number): string => {
  const url = explorerUrls[chainId.toString(10)];
  if (!url) throw new Error(`Unsupported chainId: ${chainId}`);
  return url;
};

export const getTransactionForChainId = (txHash: string, chainId: string) => {
  return `${getBlockExplorerRootUrl(chainId)}/tx/${txHash}` as const;
};

export const getAccountForChainId = (account: string, chainId: string) => {
  const accountPath =
    chainId === SOLANA_CHAIN_ID.toString(10) ? "account" : "address";
  return `${getBlockExplorerRootUrl(chainId)}/${accountPath}/${account}` as const;
};

/**
 * Non-throwing, VM-agnostic tx-explorer resolver: accepts a numeric chain id
 * or a chain-ref string (both index the same registry) and any VM's tx id
 * shape. Returns null for unmapped chains so callers can degrade to
 * copy-only rendering instead of guarding with try/catch.
 */
export const getTxExplorerUrl = (
  chainKey: string | number | null | undefined,
  txId: string,
): string | null => {
  if (chainKey === null || chainKey === undefined || txId.length === 0) {
    return null;
  }
  const url = explorerUrls[chainKey.toString()];
  return url ? `${url}/tx/${txId}` : null;
};

export const isAddressNativeTokenPlaceholder = (addy: string) => {
  const serializedAddress = serializeAddress(addy);
  if (serializedAddress === zeroAddress) {
    console.warn(
      "isAddressNativeTokenPlaceholder: zeroAddress is deprecated. Use NATIVE_TOKEN_INTERNAL_PLACEHOLDER_ADDRESS (0xeee...eee) instead.",
    );
  }
  return (
    addy.toLowerCase() === NATIVE_TOKEN_INTERNAL_PLACEHOLDER_ADDRESS ||
    addy.toLowerCase() === zeroAddress
  );
};

export const monkeyPatchNativeAddressToWrapped = (
  addy: `0x${string}`,
  chainId: string,
): `0x${string}` => {
  if (
    addy.toLowerCase() === NATIVE_TOKEN_INTERNAL_PLACEHOLDER_ADDRESS ||
    addy.toLowerCase() === zeroAddress
  ) {
    if (chainId === arbitrum.id.toString(10)) {
      return WETH_ARBITRUM_ADDRESS; // HACK(johnrjj) - Arbitrum ETH
    }
    if (chainId === base.id.toString(10)) {
      return WETH_BASE_ADDRESS;
    }
    if (chainId === polygon.id.toString(10)) {
      return WETH_POLYGON_ADDRESS; // BUG(johnrjj) - hmm? shouldnt this be...wmatic?
    }
    if (chainId === optimism.id.toString(10)) {
      return WETH_OPTIMISM_ADDRESS;
    }
    if (chainId === blast.id.toString(10)) {
      return WETH_BLAST_ADDRESS;
    }
    if (chainId === mainnet.id.toString(10)) {
      return WETH_MAINNET_ADDRESS;
    }
    if (chainId === linea.id.toString(10)) {
      return WETH_LINEA_ADDRESS;
    }
    if (chainId === unichain.id.toString(10)) {
      return WETH_UNICHAIN_ADDRESS;
    }
    if (chainId === bsc.id.toString(10)) {
      return WBNB_BSC_ADDRESS;
    }
    if (chainId === plumeMainnet.id.toString(10)) {
      return WPLUME_PLUME_ADDRESS;
    }
    if (chainId === robinhoodChain.id.toString(10)) {
      return WETH_ROBINHOOD_ADDRESS;
    }
    if (chainId === hyperevmChain.id.toString(10)) {
      return WHYPE_HYPEREVM_ADDRESS;
    }
    if (chainId === monadChain.id.toString(10)) {
      return WMON_MONAD_ADDRESS;
    }
    throw new Error(
      `ETH_PLACEHOLDER_ADDRESS not supported for chainId: ${chainId}`,
    );
  }
  return addy;
};

// Config for the app - these are the environment variable-based config
// Declare window type for environment detection without requiring DOM types
declare const window: unknown;
export const isServerFn = () => typeof window === "undefined";
export const isClientFn = () => !isServerFn();
export const assetServer = () =>
  invariant(isServerFn(), "function can only run on server");
const readRuntimeEnv = (name: string): string | undefined =>
  getRuntimeEnvRecord()[name];

export const getLifiEntrypointContractAddressByChainId = (
  chainId: string | number,
) => {
  if (chainId.toString() === ROBINHOOD_CHAIN_ID.toString()) {
    return LIFI_ROBINHOOD_ENTRYPOINT;
  }

  if (chainId.toString() === HYPEREVM_CHAIN_ID.toString()) {
    return LIFI_HYPEREVM_ENTRYPOINT;
  }

  if (chainId.toString() === MONAD_CHAIN_ID.toString()) {
    return LIFI_MONAD_ENTRYPOINT;
  }

  return LIFI_ENTRYPOINT;
};

export const getLifiApiRootUrl = (isClient = isClientFn()) => {
  if (isClient) {
    const clientApiRootUrl = readRuntimeEnv("NEXT_PUBLIC_LIFI_API_ROOT_URI");
    if (!clientApiRootUrl) {
      throw new Error("NEXT_PUBLIC_LIFI_API_ROOT_URI is not set");
    }
    return clientApiRootUrl;
  }
  assetServer();
  const serverApiRootUrl = readRuntimeEnv("LIFI_API_ROOT_URI");
  if (!serverApiRootUrl) {
    throw new Error("LIFI_API_ROOT_URI is not set");
  }
  return serverApiRootUrl;
};

export const getDefinedApiKey = (isClient: boolean = isClientFn()) => {
  if (isClient) {
    throw new Error(
      "Client-side DefinedFi API access is disabled; use platform-api market data routes.",
    );
  }
  assetServer();
  const serverDefinedApiKey = readRuntimeEnv("DEFINED_API_KEY");
  if (!serverDefinedApiKey) {
    throw new Error("DEFINED_API_KEY is not set");
  }
  return serverDefinedApiKey;
};

export const getDefinedApiRootUri = () => {
  return DEFINED_API_ROOT_URI;
};

export const getTemporalCloudAppAddress = () => {
  assetServer();
  const t = readRuntimeEnv("TEMPORAL_CLOUD_APP_ADDRESS");
  if (!t) {
    throw new Error("TEMPORAL_CLOUD_APP_ADDRESS is not set");
  }
  return t;
};

export const getTemporalCloudAppNamespace = () => {
  assetServer();
  const t = readRuntimeEnv("TEMPORAL_CLOUD_APP_NAMESPACE");
  if (!t) {
    throw new Error("TEMPORAL_CLOUD_APP_NAMESPACE is not set");
  }
  return t;
};

export const getStrategyRebalanceWorkflowType = () => {
  assetServer();
  return GliderWorkflowType.EXECUTE_STRATEGY_REBALANCE;
};

export const getStrategySnapshotWorkflowType = () => {
  assetServer();
  return GliderWorkflowType.STRATEGY_SNAPSHOT;
};

export const getStrategyWithdrawWorkflowType = () => {
  assetServer();
  return GliderWorkflowType.QUEUE_WITHDRAW;
};

export const getJsonRpcClient = (chainId: string | number) =>
  createPublicClient({
    transport: http(getJsonRpcUrlForChainId(chainId)),
    chain: getChainForChainId(chainId) as Chain,
  });

const readEip155JsonRpcUrl = (
  env: ReturnType<typeof getRuntimeEnvRecord>,
  chainId: string,
  isClient: boolean,
): string | undefined =>
  readFirstRuntimeEnvValue(
    env,
    isClient
      ? [
          eip155RpcEnvName(chainId, "HTTPS"),
          `NEXT_PUBLIC_JSON_RPC_URL_${chainId}`,
        ]
      : [eip155RpcEnvName(chainId, "HTTPS"), `JSON_RPC_URL_${chainId}`],
  );

const readEip155RoleRpcUrl = (
  env: ReturnType<typeof getRuntimeEnvRecord>,
  chainId: string,
  role: "BUNDLER" | "PAYMASTER" | "ULTRA_RELAY",
  legacyPrefix: "BUNDLER" | "PAYMASTER" | "ULTRA_RELAY",
  isClient: boolean,
): string | undefined =>
  readFirstRuntimeEnvValue(
    env,
    isClient
      ? [
          eip155RoleRpcEnvName(chainId, role, "HTTPS"),
          `NEXT_PUBLIC_${legacyPrefix}_RPC_URL_${chainId}`,
        ]
      : [
          eip155RoleRpcEnvName(chainId, role, "HTTPS"),
          `${legacyPrefix}_RPC_URL_${chainId}`,
        ],
  );

const readSolanaJsonRpcUrl = (
  env: ReturnType<typeof getRuntimeEnvRecord>,
  isClient: boolean,
): string | undefined =>
  readFirstRuntimeEnvValue(
    env,
    isClient
      ? [
          solanaMainnetRpcEnvName("HTTPS"),
          solanaRpcEnvName("HTTPS"),
          "NEXT_PUBLIC_SOLANA_MAINNET_RPC_URL",
        ]
      : [
          solanaMainnetRpcEnvName("HTTPS"),
          solanaRpcEnvName("HTTPS"),
          "SOLANA_RPC_URL",
        ],
  );

export const getJsonRpcUrlForChainId = (
  _chainId: string | number,
  isClient = isClientFn(),
) => {
  const env = getRuntimeEnvRecord();
  const chainId =
    typeof _chainId === "number" ? _chainId.toString(10) : _chainId;
  switch (chainId) {
    case polygon.id.toString(10):
      if (isClient) {
        return readEip155JsonRpcUrl(env, chainId, true);
      }
      assetServer();
      return readEip155JsonRpcUrl(env, chainId, false);
    case base.id.toString(10):
      if (isClient) {
        return readEip155JsonRpcUrl(env, chainId, true);
      }
      assetServer();
      return readEip155JsonRpcUrl(env, chainId, false);
    case arbitrum.id.toString(10):
      if (isClient) {
        return readEip155JsonRpcUrl(env, chainId, true);
      }
      assetServer();
      return readEip155JsonRpcUrl(env, chainId, false);
    case optimism.id.toString(10):
      if (isClient) {
        return readEip155JsonRpcUrl(env, chainId, true);
      }
      assetServer();
      return readEip155JsonRpcUrl(env, chainId, false);
    case blast.id.toString(10):
      if (isClient) {
        return readEip155JsonRpcUrl(env, chainId, true);
      }
      assetServer();
      return readEip155JsonRpcUrl(env, chainId, false);
    case mainnet.id.toString(10):
      if (isClient) {
        return readEip155JsonRpcUrl(env, chainId, true);
      }
      assetServer();
      return readEip155JsonRpcUrl(env, chainId, false);
    case linea.id.toString(10):
      if (isClient) {
        return readEip155JsonRpcUrl(env, chainId, true);
      }
      assetServer();
      return readEip155JsonRpcUrl(env, chainId, false);
    case unichain.id.toString(10):
      if (isClient) {
        return readEip155JsonRpcUrl(env, chainId, true);
      }
      assetServer();
      return readEip155JsonRpcUrl(env, chainId, false);
    case SOLANA_NETWORK_ID.toString():
      if (isClient) {
        return readSolanaJsonRpcUrl(env, true);
      }
      assetServer();
      return readSolanaJsonRpcUrl(env, false);
    case bsc.id.toString(10):
      if (isClient) {
        return readEip155JsonRpcUrl(env, chainId, true);
      }
      assetServer();
      return readEip155JsonRpcUrl(env, chainId, false);
    case plumeMainnet.id.toString(10):
      if (isClient) {
        return readEip155JsonRpcUrl(env, chainId, true);
      }
      assetServer();
      return readEip155JsonRpcUrl(env, chainId, false);
    case robinhoodChain.id.toString(10):
      if (isClient) {
        return readEip155JsonRpcUrl(env, chainId, true);
      }
      assetServer();
      return readEip155JsonRpcUrl(env, chainId, false);
    case hyperevmChain.id.toString(10):
      if (isClient) {
        return readEip155JsonRpcUrl(env, chainId, true);
      }
      assetServer();
      return readEip155JsonRpcUrl(env, chainId, false);
    case monadChain.id.toString(10):
      if (isClient) {
        return readEip155JsonRpcUrl(env, chainId, true);
      }
      assetServer();
      return readEip155JsonRpcUrl(env, chainId, false);
    default:
      throw new Error(`Unsupported chainId: ${chainId}`);
  }
};

export const getPaymasterRpcUrlForChainId = (
  chainId: string,
  isClient = isClientFn(),
) => {
  const env = getRuntimeEnvRecord();
  switch (chainId) {
    case polygon.id.toString(10):
      if (isClient) {
        return readEip155RoleRpcUrl(
          env,
          chainId,
          "PAYMASTER",
          "PAYMASTER",
          true,
        );
      }
      assetServer();
      return readEip155RoleRpcUrl(
        env,
        chainId,
        "PAYMASTER",
        "PAYMASTER",
        false,
      );
    case base.id.toString(10):
      if (isClient) {
        return readEip155RoleRpcUrl(
          env,
          chainId,
          "PAYMASTER",
          "PAYMASTER",
          true,
        );
      }
      assetServer();
      return readEip155RoleRpcUrl(
        env,
        chainId,
        "PAYMASTER",
        "PAYMASTER",
        false,
      );
    case arbitrum.id.toString(10):
      if (isClient) {
        return readEip155RoleRpcUrl(
          env,
          chainId,
          "PAYMASTER",
          "PAYMASTER",
          true,
        );
      }
      assetServer();
      return readEip155RoleRpcUrl(
        env,
        chainId,
        "PAYMASTER",
        "PAYMASTER",
        false,
      );
    case optimism.id.toString(10):
      if (isClient) {
        return readEip155RoleRpcUrl(
          env,
          chainId,
          "PAYMASTER",
          "PAYMASTER",
          true,
        );
      }
      assetServer();
      return readEip155RoleRpcUrl(
        env,
        chainId,
        "PAYMASTER",
        "PAYMASTER",
        false,
      );
    case blast.id.toString(10):
      if (isClient) {
        return readEip155RoleRpcUrl(
          env,
          chainId,
          "PAYMASTER",
          "PAYMASTER",
          true,
        );
      }
      assetServer();
      return readEip155RoleRpcUrl(
        env,
        chainId,
        "PAYMASTER",
        "PAYMASTER",
        false,
      );
    case mainnet.id.toString(10):
      if (isClient) {
        return readEip155RoleRpcUrl(
          env,
          chainId,
          "PAYMASTER",
          "PAYMASTER",
          true,
        );
      }
      assetServer();
      return readEip155RoleRpcUrl(
        env,
        chainId,
        "PAYMASTER",
        "PAYMASTER",
        false,
      );
    case linea.id.toString(10):
      if (isClient) {
        return readEip155RoleRpcUrl(
          env,
          chainId,
          "PAYMASTER",
          "PAYMASTER",
          true,
        );
      }
      assetServer();
      return readEip155RoleRpcUrl(
        env,
        chainId,
        "PAYMASTER",
        "PAYMASTER",
        false,
      );
    case unichain.id.toString(10):
      if (isClient) {
        return readEip155RoleRpcUrl(
          env,
          chainId,
          "PAYMASTER",
          "PAYMASTER",
          true,
        );
      }
      assetServer();
      return readEip155RoleRpcUrl(
        env,
        chainId,
        "PAYMASTER",
        "PAYMASTER",
        false,
      );
    case bsc.id.toString(10):
      if (isClient) {
        return readEip155RoleRpcUrl(
          env,
          chainId,
          "PAYMASTER",
          "PAYMASTER",
          true,
        );
      }
      assetServer();
      return readEip155RoleRpcUrl(
        env,
        chainId,
        "PAYMASTER",
        "PAYMASTER",
        false,
      );
    case plumeMainnet.id.toString(10):
      if (isClient) {
        return readEip155RoleRpcUrl(
          env,
          chainId,
          "PAYMASTER",
          "PAYMASTER",
          true,
        );
      }
      assetServer();
      return readEip155RoleRpcUrl(
        env,
        chainId,
        "PAYMASTER",
        "PAYMASTER",
        false,
      );
    case robinhoodChain.id.toString(10):
      if (isClient) {
        return readEip155RoleRpcUrl(
          env,
          chainId,
          "PAYMASTER",
          "PAYMASTER",
          true,
        );
      }
      assetServer();
      return readEip155RoleRpcUrl(
        env,
        chainId,
        "PAYMASTER",
        "PAYMASTER",
        false,
      );
    case hyperevmChain.id.toString(10):
      if (isClient) {
        return readEip155RoleRpcUrl(
          env,
          chainId,
          "PAYMASTER",
          "PAYMASTER",
          true,
        );
      }
      assetServer();
      return readEip155RoleRpcUrl(
        env,
        chainId,
        "PAYMASTER",
        "PAYMASTER",
        false,
      );
    case monadChain.id.toString(10):
      if (isClient) {
        return readEip155RoleRpcUrl(
          env,
          chainId,
          "PAYMASTER",
          "PAYMASTER",
          true,
        );
      }
      assetServer();
      return readEip155RoleRpcUrl(
        env,
        chainId,
        "PAYMASTER",
        "PAYMASTER",
        false,
      );
    default:
      throw new Error(`Unsupported chainId: ${chainId}`);
  }
};

export const isUltraRelayBundlerActiveForChainId = (chainId: string) => {
  const ultraRelayRpcUrl = readFirstRuntimeEnvValue(getRuntimeEnvRecord(), [
    eip155RoleRpcEnvName(chainId, "ULTRA_RELAY", "HTTPS"),
    `ULTRA_RELAY_RPC_URL_${chainId}`,
  ]);
  return (
    !!ultraRelayRpcUrl && ultraRelayRpcUrl.includes("provider=ULTRA_RELAY")
  );
};

/**
 * NOTE: Bundler must support ZeroDev RPC methods for gas estimation.
 * @param chainId
 * @param isClient
 * @returns
 */
export const getBundlerRpcUrlForChainId = (
  chainId: string,
  isClient = isClientFn(),
) => {
  const env = getRuntimeEnvRecord();
  switch (chainId) {
    case polygon.id.toString(10):
      if (isClient) {
        return readEip155RoleRpcUrl(env, chainId, "BUNDLER", "BUNDLER", true);
      }
      assetServer();
      return readEip155RoleRpcUrl(env, chainId, "BUNDLER", "BUNDLER", false);
    case base.id.toString(10):
      if (isClient) {
        return readEip155RoleRpcUrl(env, chainId, "BUNDLER", "BUNDLER", true);
      }
      assetServer();
      return (
        readEip155RoleRpcUrl(
          env,
          chainId,
          "ULTRA_RELAY",
          "ULTRA_RELAY",
          false,
        ) ?? readEip155RoleRpcUrl(env, chainId, "BUNDLER", "BUNDLER", false)
      );
    case arbitrum.id.toString(10):
      if (isClient) {
        return readEip155RoleRpcUrl(env, chainId, "BUNDLER", "BUNDLER", true);
      }
      assetServer();
      return readEip155RoleRpcUrl(env, chainId, "BUNDLER", "BUNDLER", false);
    case optimism.id.toString(10):
      if (isClient) {
        return readEip155RoleRpcUrl(env, chainId, "BUNDLER", "BUNDLER", true);
      }
      assetServer();
      return readEip155RoleRpcUrl(env, chainId, "BUNDLER", "BUNDLER", false);
    case blast.id.toString(10):
      if (isClient) {
        return readEip155RoleRpcUrl(env, chainId, "BUNDLER", "BUNDLER", true);
      }
      assetServer();
      return readEip155RoleRpcUrl(env, chainId, "BUNDLER", "BUNDLER", false);
    case mainnet.id.toString(10):
      if (isClient) {
        return readEip155RoleRpcUrl(env, chainId, "BUNDLER", "BUNDLER", true);
      }
      assetServer();
      return readEip155RoleRpcUrl(env, chainId, "BUNDLER", "BUNDLER", false);
    case linea.id.toString(10):
      if (isClient) {
        return readEip155RoleRpcUrl(env, chainId, "BUNDLER", "BUNDLER", true);
      }
      assetServer();
      return readEip155RoleRpcUrl(env, chainId, "BUNDLER", "BUNDLER", false);
    case unichain.id.toString(10):
      if (isClient) {
        return readEip155RoleRpcUrl(env, chainId, "BUNDLER", "BUNDLER", true);
      }
      assetServer();
      return readEip155RoleRpcUrl(env, chainId, "BUNDLER", "BUNDLER", false);
    case bsc.id.toString(10):
      if (isClient) {
        return readEip155RoleRpcUrl(env, chainId, "BUNDLER", "BUNDLER", true);
      }
      assetServer();
      return (
        readEip155RoleRpcUrl(
          env,
          chainId,
          "ULTRA_RELAY",
          "ULTRA_RELAY",
          false,
        ) ?? readEip155RoleRpcUrl(env, chainId, "BUNDLER", "BUNDLER", false)
      );
    case plumeMainnet.id.toString(10):
      if (isClient) {
        return readEip155RoleRpcUrl(env, chainId, "BUNDLER", "BUNDLER", true);
      }
      assetServer();
      return (
        readEip155RoleRpcUrl(
          env,
          chainId,
          "ULTRA_RELAY",
          "ULTRA_RELAY",
          false,
        ) ?? readEip155RoleRpcUrl(env, chainId, "BUNDLER", "BUNDLER", false)
      );
    case robinhoodChain.id.toString(10):
      if (isClient) {
        return readEip155RoleRpcUrl(env, chainId, "BUNDLER", "BUNDLER", true);
      }
      assetServer();
      return (
        readEip155RoleRpcUrl(
          env,
          chainId,
          "ULTRA_RELAY",
          "ULTRA_RELAY",
          false,
        ) ?? readEip155RoleRpcUrl(env, chainId, "BUNDLER", "BUNDLER", false)
      );
    case hyperevmChain.id.toString(10):
      if (isClient) {
        return readEip155RoleRpcUrl(env, chainId, "BUNDLER", "BUNDLER", true);
      }
      assetServer();
      return (
        readEip155RoleRpcUrl(
          env,
          chainId,
          "ULTRA_RELAY",
          "ULTRA_RELAY",
          false,
        ) ?? readEip155RoleRpcUrl(env, chainId, "BUNDLER", "BUNDLER", false)
      );
    case monadChain.id.toString(10):
      if (isClient) {
        return readEip155RoleRpcUrl(env, chainId, "BUNDLER", "BUNDLER", true);
      }
      assetServer();
      return (
        readEip155RoleRpcUrl(
          env,
          chainId,
          "ULTRA_RELAY",
          "ULTRA_RELAY",
          false,
        ) ?? readEip155RoleRpcUrl(env, chainId, "BUNDLER", "BUNDLER", false)
      );
    default:
      throw new Error(`Unsupported chainId: ${chainId}`);
  }
};

export const getCoinbaseBundlerRpcUrl = (chainId: string) => {
  assetServer();
  if (!isCoinbaseBundlerChainSupported(chainId)) {
    throw new Error(
      `Unsupported chainId for Coinbase bundler: ${chainId}. Base-only by policy currently.`,
    );
  }

  return readRuntimeEnv("COINBASE_BUNDLER_RPC_URL_8453");
};

export const getBundlerRpcClient = (chainId: string) =>
  createPublicClient({
    // entryPoint: ENTRYPOINT_ADDRESS_V07,
    chain: getChainForChainId(chainId) as Chain, // KLUDGE(johnrjj) - viem type is off for this (complains about Chain | undefined?)
    transport: http(getBundlerRpcUrlForChainId(chainId)),
  });

// NOTE: the rebalance minimum-notional floor lives in @repo/constants
// (REBALANCE_THRESHOLD_USD_MIN + REBALANCE_THRESHOLD_USD_CHAIN_MIN, accessed via
// getMinNotionalUsdForChain). The former MIN_REBALANCE_THRESHOLD_USD here was an
// unused duplicate and has been removed.

export const MIN_WITHDRAWAL_USD_PRICE_FLUCTUATION_ALLOWED = 0.05;

// Minimum threshold for SVM (Solana) withdrawals (in USD, per asset)
export const MIN_SVM_WITHDRAWAL_USD = 1;
export const ACTUAL_MIN_SVM_WITHDRAWAL_USD =
  MIN_SVM_WITHDRAWAL_USD - MIN_WITHDRAWAL_USD_PRICE_FLUCTUATION_ALLOWED;

// Minimum thresholds for swap operations (in USD)
export const DEFAULT_SWAP_USD_AMOUNT_THRESHOLD_SAME_CHAIN = 1.0;
export const DEFAULT_SWAP_USD_AMOUNT_THRESHOLD_CROSS_CHAIN = 5;

// Minimum thresholds for bridging (raw amount)
export const MIN_ETH_BRIDGE_AMOUNT = parseEther("0.001"); // 0.001 ETH in wei (~$5)
export const MIN_BNB_BRIDGE_AMOUNT = parseEther("0.01"); // 0.01 BNB in wei (~$10)
export const MIN_USD_BRIDGE_AMOUNT = parseUnits(
  DEFAULT_SWAP_USD_AMOUNT_THRESHOLD_CROSS_CHAIN.toString(),
  6,
); // 5 USDC/USDT (6 decimals)
